/**
 * Feature Ideas module API client (T35.7.1).
 *
 * Thin wrappers for the standalone `/api/feature-ideas` endpoints
 * (published-only cards, 👍/👎 vote + "why", similar-search, propose). Standalone
 * feature (ADR-012): nothing here imports a shared feedback corpus or the donation
 * surface — the subscription client lives in `features/subscriptions/api.ts` (T35.7.2 Part A).
 *
 * Auth: the app shell injects its shared HttpClient, including the native-aware
 * TokenProvider and the 401 refresh-and-retry path.
 *
 * CP1/CP4: the accessor is injected from the app shell — no platform-shaped code
 * (no Capacitor import) crosses into this module.
 */

import { ApiError, type HttpClient } from '@shopista/shared/api';

let client: HttpClient | null = null;

export function setFeatureIdeasHttpClient(httpClient: HttpClient): void {
  client = httpClient;
}

function http(): HttpClient {
  if (!client) throw new Error('Feature Ideas HTTP client has not been configured');
  return client;
}

function legacyError(error: unknown, fallback: string): never {
  if (error instanceof ApiError) {
    const err = new Error(fallback) as Error & { status?: number };
    err.status = error.status;
    throw err;
  }
  throw error;
}

/** The directional vote on a published idea. */
export type VoteDirection = 'up' | 'down';

/** One published feature-idea card (the Feedback-tab list). */
export interface FeatureIdeaCard {
  id: string;
  title: string;
  description: string | null;
  /** Always 0 — the standalone re-cut dropped the source-report provenance. */
  from: number;
  /** Live upvote count. */
  upvotes: number;
  /** The caller's current vote, or null. */
  my_vote: VoteDirection | null;
}

/** The vote result after casting / changing / clearing. */
export interface VoteResult {
  my_vote: VoteDirection | null;
  upvotes: number;
}

/**
 * One similar-search match for the propose sheet. Carries the SAME shape as a
 * `FeatureIdeaCard` because a match renders as the FULL interactive idea card —
 * identical to the published list — not a bare title+upvote row.
 */
export interface IdeaSearchMatch {
  id: string;
  title: string;
  description: string | null;
  /** Always 0 — the standalone re-cut dropped the source-report provenance. */
  from: number;
  upvotes: number;
  /** The caller's current vote, or null. */
  my_vote: VoteDirection | null;
}

/** The result of saving a "why" — the vote row it landed on. */
export interface SaveWhyResult {
  vote_id: string;
}

/** The result of proposing a new idea. */
export interface ProposeResult {
  id: string;
}

/** Shared GET helper for the feature-ideas endpoints. */
async function getJson<T>(path: string): Promise<T> {
  try { return await http().getRaw(path) as T; }
  catch (error) { return legacyError(error, `GET ${path} failed: ${(error as ApiError).status}`); }
}

/** Shared POST helper for the feature-ideas endpoints. */
async function postJson<T>(path: string, body: unknown): Promise<T> {
  try { return await http().postRaw(path, body) as T; }
  catch (error) { return legacyError(error, `POST ${path} failed: ${(error as ApiError).status}`); }
}

/** GET the published feature ideas (held/archived never returned). */
export async function getFeatureIdeas(): Promise<FeatureIdeaCard[]> {
  const body = await getJson<{ ideas: FeatureIdeaCard[] }>('/api/feature-ideas');
  return body.ideas;
}

/** Cast / change / clear the caller's vote on a published idea (re-tap clears). */
export function voteFeatureIdea(id: string, direction: VoteDirection): Promise<VoteResult> {
  return postJson<VoteResult>(`/api/feature-ideas/${id}/vote`, { direction });
}

/** Save the post-vote free-text "why" on the vote row (no corpus write). */
export function saveFeatureIdeaWhy(id: string, text: string): Promise<SaveWhyResult> {
  return postJson<SaveWhyResult>(`/api/feature-ideas/${id}/why`, { text });
}

/** Search published ideas for the propose sheet (so the user upvotes a match, not a dup). */
export async function searchFeatureIdeas(q: string): Promise<IdeaSearchMatch[]> {
  const body = await getJson<{ matches: IdeaSearchMatch[] }>(
    `/api/feature-ideas/search?q=${encodeURIComponent(q)}`,
  );
  return body.matches;
}

/** Propose a brand-new idea (mints a held, new-suggestion idea). */
export function proposeFeatureIdea(text: string): Promise<ProposeResult> {
  return postJson<ProposeResult>('/api/feature-ideas/propose', { text });
}
